import { Button, Dialog, DialogBody, DialogContent, DialogFooter, FullScreenLoading } from '@civilgrid/ui/components';
import { GoogleMap, StreetViewPanorama, useJsApiLoader } from '@react-google-maps/api';
import { nanoid } from 'nanoid';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { CivilDrawObjectType, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project, StreetViewMarkerObject } from '~/common/types';
import type { MapStoreBase } from '~/components2/Map';
import getConfig from '~/config';
import type { CivilDrawStoreApi } from '../civilDrawStoreContext';

const config = getConfig();

interface Props {
  store: MapStoreBase;
}

export default function StreetView({ store }: Props) {
  const [panorama, setPanorama] = useState<null | google.maps.StreetViewPanorama>(null);
  const [isAvailable, setAvailability] = useState(true);
  // TODO: projectId & currentMapId values are present. Type just needs to get updated.
  const { streetViewState, projectId, currentMapId, project } = useSnapshot(store as CivilDrawStoreApi);
  const { current: map } = useMap();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMapsApiToken,
  });

  const [isStreetViewLoading, setIsStreetViewLoading] = useState(false);

  useEffect(() => {
    setIsStreetViewLoading(true);

    if (panorama) {
      setTimeout(() => {
        if (panorama.getLocation()) {
          setAvailability(true);
        } else {
          setAvailability(false);
        }

        setIsStreetViewLoading(false);
      }, 1000);
    }
  }, [panorama]);

  let lng = 0,
    lat = 0,
    pitch = 0,
    heading = 0,
    zoom = 1;
  if (streetViewState.visible) {
    if (streetViewState.isNew) {
      ({ lng, lat, pitch, heading, zoom } = streetViewState);
    } else {
      ({ pitch, heading, zoom } = streetViewState.marker.properties);
      lng = streetViewState.marker.geometry.coordinates[0];
      lat = streetViewState.marker.geometry.coordinates[1];
    }
  }

  const createBookmark = async () => {
    const pov = panorama!.getPov();
    const zoom = panorama!.getZoom();
    const location = panorama!.getLocation();
    if (location?.latLng) {
      store.newObject(
        {
          id: nanoid(),
          type: 'streetViewMarker',
          title: location.shortDescription || '',
          sortOrder: store.generateSortOrderKey(),
          description: location.description || '',
          geometry: {
            type: 'Point',
            coordinates: [location.latLng!.lng(), location.latLng!.lat()],
          },
          properties: {
            heading: pov.heading,
            pitch: pov.pitch,
            zoom,
          },
        },
        false,
      );
      store.setStreetViewState({ visible: false });
    } else {
      alert('Bookmark creation failed');
    }
  };

  const deleteBookmark = async () => {
    store.deleteObject(
      (
        streetViewState as {
          visible: true;
          isNew: false;
          marker: StreetViewMarkerObject;
        }
      ).marker,
      true,
    );
    store.setStreetViewState({ visible: false });
  };

  return isLoaded && streetViewState.visible ? (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) store.setStreetViewState({ visible: false });
      }}
    >
      <DialogContent
        title="Street View"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="civildraw-streetview-dialog"
        // Opened from the map's context menu or a map marker, so focus returns to the map.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          map?.getCanvas().focus();
        }}
      >
        <DialogBody className="text-sm">
          {isStreetViewLoading && <FullScreenLoading />}
          {isAvailable ? (
            <GoogleMap
              center={{ lng, lat }}
              zoom={14}
              mapContainerStyle={{
                width: window.innerWidth * 0.7,
                height: window.innerHeight * 0.7,
              }}
            >
              <StreetViewPanorama
                onLoad={(p) => setPanorama(p)}
                options={{
                  visible: true,
                  enableCloseButton: false,
                  zoom: zoom,
                  position: {
                    lng: lng,
                    lat: lat,
                  },
                  pov: {
                    heading: heading,
                    pitch: pitch,
                  },
                  imageDateControl: true,
                }}
              />
            </GoogleMap>
          ) : (
            <StreetViewStateBlock>Street View is not available at this location.</StreetViewStateBlock>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            data-testid="civildraw-streetview-close"
            onClick={() => {
              store.setStreetViewState({ visible: false });
              setAvailability(true);
            }}
            autoFocus
          >
            Close
          </Button>
          {streetViewState.isNew && isAvailable && (
            <Button
              data-testid="civildraw-streetview-bookmark"
              onClick={() => {
                // `project_civildraw_object_added` is fired by store.newObject (called inside
                // createBookmark); don't fire it again here or it double-counts.
                createBookmark();
              }}
            >
              Bookmark
            </Button>
          )}
          {!streetViewState.isNew && (
            <Button
              variant={'danger'}
              data-testid="civildraw-streetview-delete-bookmark"
              onClick={() => {
                deleteBookmark();
                const analyticsProject = project as unknown as Project;
                captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectDeleted, {
                  project_id: analyticsProject.id,
                  organization_id: analyticsProject.organization || undefined,
                  object_type: CivilDrawObjectType.StreetViewMarker,
                  ...(currentMapId && { map_id: currentMapId }),
                  civildraw_use_new_drawing_canvas: false,
                });
              }}
            >
              Delete Bookmark
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;
}

const StreetViewStateBlock = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div style={{ width: screen.width / 3, height: screen.height / 3 }} className="flex items-center justify-center">
      <h1 className="text-lg">{children}</h1>
    </div>
  );
};
